import { Unit, UnitsMultilevelList } from '@/types';

const COLLAPSE_THRESHOLD = 3;
const MAX_SHOWN = 2;

const isNumeric = (s: string) => /^\d+$/.test(s.trim());

type FlatUnit = Unit & { children?: Unit[] };

const findUnit = (list: FlatUnit[], code: string): Unit | null => {
  for (const item of list) {
    if (item.code === code) return item;
    if (item.children?.length) {
      const found = findUnit(item.children as FlatUnit[], code);
      if (found) return found;
    }
  }
  return null;
};

/** Returns only the deepest selected codes — drops a code if any other selected code is a child of it. */
const getDeepestCodes = (codes: string[]): string[] =>
  codes.filter((code) => !codes.some((other) => other !== code && other.startsWith(code + '.')));

export const getFormatSelectedUnits = (
  selectedCodes: string[],
  unitList: UnitsMultilevelList,
  _t: (key: string) => string,
  andMore?: (count: number) => string,
): string => {
  if (!selectedCodes.length) return '';

  const deepest = getDeepestCodes(Array.from(new Set(selectedCodes)));

  type Resolved = { unit: Unit; code: string; level: number };
  const resolved: Resolved[] = deepest
    .map((code) => {
      const unit = findUnit(unitList as unknown as FlatUnit[], code);
      return unit ? { unit, code, level: code.split('.').length } : null;
    })
    .filter((x): x is Resolved => !!x);

  if (!resolved.length) return '';

  // Group by parent for collapse logic
  const groups = new Map<string, { parentTitle: string; names: string[] }>();
  for (const { unit, code, level } of resolved) {
    let parentTitle = '';
    if (level >= 2) {
      const parentCode = code
        .split('.')
        .slice(0, level - 1)
        .join('.');
      const parentUnit = findUnit(unitList as unknown as FlatUnit[], parentCode);
      parentTitle = parentUnit?.title ?? '';
    }

    const entry = groups.get(parentTitle) ?? { parentTitle, names: [] };
    entry.names.push(unit.title);
    groups.set(parentTitle, entry);
  }

  const labels: string[] = [];
  for (const { parentTitle, names } of groups.values()) {
    if (names.length >= COLLAPSE_THRESHOLD) {
      const label =
        parentTitle && !isNumeric(parentTitle) ? `${parentTitle} (${names.length})` : '';
      if (label) labels.push(label);
      else for (const name of names) if (!isNumeric(name)) labels.push(name);
    } else {
      const namedItems = names.filter((name) => !isNumeric(name));
      if (namedItems.length) {
        for (const name of namedItems) labels.push(name);
      } else if (parentTitle && !isNumeric(parentTitle)) {
        labels.push(parentTitle);
      }
    }
  }

  if (!labels.length) return '';
  if (labels.length <= MAX_SHOWN) return labels.join(', ');
  const overflow = labels.length - MAX_SHOWN;
  const suffix = andMore ? ` ${andMore(overflow)}` : '';
  return `${labels.slice(0, MAX_SHOWN).join(', ')}${suffix}`;
};
